import { forwardRef, FunctionComponent, ReactNode, SVGProps, useState } from "react";
import { FloatingPopover } from "UIPalette/FloatingPopover/FloatingPopover";

type MenuTheme = "green" | "orange" | "purple" |  "red" ;

export type MenuItem = {
  emoji: FunctionComponent<SVGProps<SVGSVGElement>>;
  title: string;
  theme: MenuTheme;
  onClick: () => void;
};

type CardMenuListItemProps = MenuItem & { selected: boolean };

export type CardMenuListCoachmark = {
  itemIndex: number;
  initialOpenState: boolean;
  content: (props: { dismiss: () => void }) => ReactNode;
};

type CardMenuListProps = { menuItems: MenuItem[]; timeoutDuration?: number; coachmark?: CardMenuListCoachmark };

const CardMenuListItem = forwardRef<HTMLButtonElement, CardMenuListItemProps>(({ title, emoji, theme, onClick, selected }, ref) => {
  const selectedTheme: { activeStyles: string; activeTextColour: string } = {
    green: {
      activeStyles: "border-v-green bg-v-green",
      activeTextColour: "!text-white",
    },
    orange: {
      activeStyles: "border-brand-orange bg-brand-orange",
      activeTextColour: "!text-white"
    },
    purple: {
      activeStyles: "border-brand-purple bg-brand-purple",
      activeTextColour: "!text-white"
    },
     red: {
      activeStyles: "border-v-red bg-v-red",
      activeTextColour: "!text-white"
    }
  }[theme];

   const Emoji = emoji;

   return <button
          ref={ref}
          onClick={onClick}
          className={`rounded-lg border-2 p-6 shadow-md transition-all ${
            selected
              ? selectedTheme.activeStyles
              : "border-brand-grey-light bg-white text-font-dark"
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Emoji aria-hidden className="w-6 h-6" />
            <h3 className={`text-lg font-semibold text-center !pb-0 ${ selected ? selectedTheme.activeTextColour : "text-navy-blue" }`}  >
              {title}
            </h3>
          </div>
        </button>
});

export const CardMenuList = ({ menuItems, timeoutDuration = 2000, coachmark }: CardMenuListProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isWaiting, setIsWaiting] = useState(false);

  const handleClick = (index: number, callback: () => void) => {
    if (isWaiting) {
      return;
    }

    setSelectedIndex(index);
    setIsWaiting(true);

    setTimeout(() => {
      callback();
      setSelectedIndex(null);
      setIsWaiting(false);
    }, timeoutDuration);
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {menuItems.map((item, index) => {
        if (coachmark?.itemIndex !== index) {
          return (
            <CardMenuListItem key={item.title} {...item} onClick={() => handleClick(index, item.onClick)} selected={selectedIndex === index} />
          );
        }

        return (
          <FloatingPopover
            key={item.title}
            initialOpenState={coachmark.initialOpenState}
            borderRadius={20}
            trigger={(props) => (
              <CardMenuListItem
                ref={props.ref}
                {...item}
                onClick={() => handleClick(index, item.onClick)}
                selected={selectedIndex === index}
              />
            )}
          >
            {coachmark.content}
          </FloatingPopover>
        );
      })}
    </div>
  );
};
